// web/src/player/sfxScheduler.ts — WebAudio SFX scheduler (Task 13)
//
// Port of iOS AudioSyncedAnimationEngine.checkAndPlaySoundEffects — but instead
// of firing each cue at runtime, the web pre-schedules all cues on the shared
// AudioContext clock so they fire with sample-accurate precision.
//
// Node graph per cue: AudioBufferSourceNode → GainNode(cue.volume) → destination

import type { SoundEffectCue } from '@/player/audio/types';

export class SfxScheduler {
  private ctx: AudioContext;
  private destination: AudioNode;
  /** Decoded buffers keyed by effectName. Only populated for successfully loaded effects. */
  private buffers = new Map<string, AudioBuffer>();
  /** Every source node started by schedule() — cleared by stopAll(). */
  private activeSources: AudioBufferSourceNode[] = [];

  constructor(ctx: AudioContext, destination: AudioNode) {
    this.ctx = ctx;
    this.destination = destination;
  }

  // ---------------------------------------------------------------------------
  // preload
  // ---------------------------------------------------------------------------

  /**
   * For each DISTINCT `cue.effectName`, fetch → decode → cache in `buffers`.
   *
   * Deduplication: a name appearing in multiple cues is fetched/decoded only once.
   * Error tolerance: if fetch or decodeAudioData fails for one effect, the error is
   * swallowed and that name is simply absent from `buffers`; all other effects
   * continue to load normally.
   */
  async preload(
    cues: SoundEffectCue[],
    resolveUrl: (effectName: string) => string,
  ): Promise<void> {
    // Dedupe: collect the set of distinct effect names
    const distinctNames = [...new Set(cues.map((c) => c.effectName))];

    await Promise.all(
      distinctNames.map(async (effectName) => {
        try {
          const url = resolveUrl(effectName);
          const response = await fetch(url);
          const arrayBuffer = await response.arrayBuffer();
          const buffer = await this.ctx.decodeAudioData(arrayBuffer);
          this.buffers.set(effectName, buffer);
        } catch {
          // Swallow: this effect will be silently skipped during schedule()
        }
      }),
    );
  }

  // ---------------------------------------------------------------------------
  // schedule
  // ---------------------------------------------------------------------------

  /**
   * For each cue whose `triggerTime >= startOffsetSec` and whose buffer loaded:
   *   1. Create an AudioBufferSourceNode and assign the pre-decoded buffer.
   *   2. Create a GainNode with `gain.value = cue.volume`.
   *   3. Wire: source → gain → destination.
   *   4. Call `source.start(ctx.currentTime + (cue.triggerTime - startOffsetSec))`.
   *
   * Cues with `triggerTime < startOffsetSec` (already passed) are skipped.
   * Cues whose buffer is not in the map (failed load) are skipped.
   *
   * @param cues         The full list of SoundEffectCues for the session.
   * @param startOffsetSec  The playback position (seconds) at the moment schedule()
   *                     is called. Defaults to 0 (beginning of session).
   */
  schedule(cues: SoundEffectCue[], startOffsetSec = 0): void {
    for (const cue of cues) {
      // Skip cues that have already passed
      if (cue.triggerTime < startOffsetSec) continue;

      // Skip effects that failed to preload
      const buffer = this.buffers.get(cue.effectName);
      if (!buffer) continue;

      // Source node
      const source = this.ctx.createBufferSource();
      source.buffer = buffer;

      // Gain node
      const gain = this.ctx.createGain();
      gain.gain.value = cue.volume;

      // Wire up: source → gain → destination
      source.connect(gain);
      gain.connect(this.destination);

      // Schedule: fire at absolute AudioContext time
      source.start(this.ctx.currentTime + (cue.triggerTime - startOffsetSec));

      this.activeSources.push(source);
    }
  }

  // ---------------------------------------------------------------------------
  // playNow
  // ---------------------------------------------------------------------------

  /**
   * Play one preloaded effect immediately (runtime-triggered, e.g. a deck flip
   * click). No-op if the buffer failed to preload. Tracked so stopAll() cleans it.
   */
  playNow(effectName: string, volume: number): void {
    const buffer = this.buffers.get(effectName);
    if (!buffer) return;
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    const gain = this.ctx.createGain();
    gain.gain.value = volume;
    source.connect(gain);
    gain.connect(this.destination);
    source.start(this.ctx.currentTime);
    this.activeSources.push(source);
  }

  // ---------------------------------------------------------------------------
  // stopAll
  // ---------------------------------------------------------------------------

  /**
   * Stop every source node started by `schedule()`.
   * Guards against `InvalidStateError` (source already stopped).
   * Clears the tracking list so subsequent calls are no-ops.
   */
  stopAll(): void {
    for (const source of this.activeSources) {
      try {
        source.stop();
      } catch {
        // Already stopped — ignore InvalidStateError
      }
    }
    this.activeSources = [];
  }
}
